import React, { useEffect } from 'react';
import { ConfirmationModal } from '~/Components';
import { useTranslation } from 'react-i18next';
import { ManageBookingsMenuStyles } from './ManageBookingsMenu.styles';
import { Typography } from '~/UIKit';
import TextField from '@mui/material/TextField';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { ComboBox } from '~/UIKit/Form/ComboBox';
import { useManageBookingsConfirmationModal } from './useManageBookingsConfirmationModal';
import { useDateFns } from '~/Hooks';

export interface IManageBookingsConfirmationModal {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  closeMenu: () => void;
  title: string;
  subtitle: string;
  note: string;
  setEmailOrMemberId: (value: string) => void;
  setEventId: (value: number) => void;
  emailOrMemberId: string;
  eventId: number;
}

export const ManageBookingsConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  closeMenu,
  subtitle,
  title,
  note,
  emailOrMemberId,
  setEmailOrMemberId,
  eventId,
  setEventId,
}: IManageBookingsConfirmationModal) => {
  const { t } = useTranslation();
  const { formatDate } = useDateFns();
  const { classes } = ManageBookingsMenuStyles();
  const {
    events,
    isLoading,
    isFormValid,
    handleMembershipOrEmailInputChange,
    handleDropdownChange,
    selectedOption,
    formattedDate,
  } = useManageBookingsConfirmationModal({
    setEmailOrMemberId,
    setEventId,
    eventId,
    emailOrMemberId,
  });

  useEffect(() => {
    closeMenu();
  }, []);

  return (
    <ConfirmationModal
      open={open}
      confirm={{ action: onConfirm, disabled: !isFormValid, title: t('app.submit') }}
      cancel={{ action: onClose, title: t('app.cancel') }}
      title={title}
    >
      <div className={classes.textWrapper}>
        <Typography>{subtitle}</Typography>
        <div className={classes.note}>
          <WarningAmberIcon className={classes.iconColor} />
          <Typography>{note}</Typography>
        </div>
      </div>
      <div className={classes.eventWrapper}>
        <Typography className={classes.normalText}>
          {t('app.events.manualCheckin.checkinModal.date')}:
        </Typography>
        <Typography className={classes.date}>{formattedDate}</Typography>
      </div>
      <ComboBox
        label={t('app.selectEvent')}
        options={events!}
        loading={isLoading}
        onChange={handleDropdownChange}
        value={selectedOption}
        noOptionsText={t('app.events.manualCheckin.noOptions')}
        renderInput={(params) => (
          <TextField
            {...params}
            id="combobox"
            variant="outlined"
            className={classes.comboboxInput}
          />
        )}
        renderOptions={(props, option) => (
          <li className={classes.listItem} {...props}>
            <div>
              <Typography variant="body2" className={classes.name}>
                {option.name}
              </Typography>
              <Typography variant="body2" className={classes.listLocation}>
                {`${option.locationName} · ${
                  option.trainerNames?.length === 0 ? t('app.noTrainer') : option.trainerNames?.[0]
                }`}
              </Typography>
            </div>
            <div className={classes.listDate}>
              <Typography variant="body2">{formatDate(option.start, 'HH:mm')}</Typography>
              <Typography variant="body2">{formatDate(option.end, 'HH:mm')}</Typography>
            </div>
          </li>
        )}
        extraClasses={{
          label: classes.label,
          combobox: classes.combobox,
        }}
        openOnFocus
      />
      <Typography className={classes.label}>
        {t('app.events.manualCheckin.checkinModal.input')}
      </Typography>
      <TextField
        fullWidth
        variant="outlined"
        InputProps={{
          className: classes.membership,
        }}
        onChange={handleMembershipOrEmailInputChange}
      />
    </ConfirmationModal>
  );
};
